var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { peerService } from "./PeerService";
import { busService } from "../../bus";
import { PeerAddedMessage } from "../../../messages/session/PeerMessages";
// Quality levels ordered from lowest (0) to highest (3)
const QUALITY_LEVELS = [
    { name: "minimal", maxBitrateKbps: 150, scaleResolutionDownBy: 4 }, // ~180p
    { name: "low", maxBitrateKbps: 400, scaleResolutionDownBy: 2 }, // ~360p
    { name: "medium", maxBitrateKbps: 900, scaleResolutionDownBy: 1.5 }, // ~480p
    { name: "high", maxBitrateKbps: 2500, scaleResolutionDownBy: 1 }, // 720p
];
const CHECK_INTERVAL_MS = 4000; // check every 4 seconds
const DOWNGRADE_AFTER_N_BAD = 2; // downgrade after 2 consecutive bad samples  (~8s)
const UPGRADE_AFTER_N_GOOD = 5; // upgrade   after 5 consecutive good samples (~20s)
const PACKET_LOSS_BAD_THRESHOLD = 0.05; // > 5 % fractionLost  → bad
const PACKET_LOSS_GOOD_THRESHOLD = 0.01; // < 1 % fractionLost  → good
const RTT_BAD_S = 0.25; // > 250 ms round-trip → bad
const RTT_GOOD_S = 0.10; // < 100 ms round-trip → good
/**
 * AdaptiveBandwidthService dynamically adjusts outgoing video bitrate and resolution
 * for all WebRTC peers based on measured packet loss and round-trip time.
 *
 * Usage:
 *   adaptiveBandwidthService.start()   – begin monitoring (call when camera goes live)
 *   adaptiveBandwidthService.stop()    – stop monitoring and lift all restrictions
 */
export class AdaptiveBandwidthService {
    constructor() {
        this.currentQualityIndex = QUALITY_LEVELS.length - 1;
        this.badSamples = 0;
        this.goodSamples = 0;
        this.intervalId = null;
        this.active = false;
        this.bus = busService.connection("AdaptiveBandwidthService");
        // ── Event handlers ────────────────────────────────────────────────────────
        this.onPeerAdded = (msg) => {
            if (!this.active)
                return;
            // Apply the current quality level whenever a new peer connects.
            // Small delay to let the transport finish setting up.
            window.setTimeout(() => {
                this.applyQualityToPeer(msg.clientId, this.currentQualityIndex);
            }, 2000);
        };
        // Subscribe once; the active flag gates processing when the service is stopped
        this.bus.subscribeByType(PeerAddedMessage, this.onPeerAdded);
    }
    start() {
        if (this.active)
            return;
        this.active = true;
        this.currentQualityIndex = QUALITY_LEVELS.length - 1;
        this.badSamples = 0;
        this.goodSamples = 0;
        this.intervalId = window.setInterval(() => {
            void this.checkAndAdapt();
        }, CHECK_INTERVAL_MS);
        console.log("[adaptive-bandwidth] started");
    }
    stop() {
        if (!this.active)
            return;
        this.active = false;
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        // Lift all restrictions so the stream runs at full quality once the camera is off
        this.applyQualityToAllPeers(QUALITY_LEVELS.length - 1);
        console.log("[adaptive-bandwidth] stopped");
    }
    // ── Core adaptation loop ──────────────────────────────────────────────────
    checkAndAdapt() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const peers = (_a = peerService.peerMgr) === null || _a === void 0 ? void 0 : _a.peers;
            if (peers == null || peers.size === 0)
                return;
            let totalFractionLost = 0;
            let totalRtt = 0;
            let sampleCount = 0;
            for (const [, peer] of peers) {
                const peerConn = peer.webRTC.getPeerConnection();
                if (peerConn == null)
                    continue;
                try {
                    const stats = yield peerConn.getStats();
                    stats.forEach((report) => {
                        var _a, _b;
                        // remote-inbound-rtp is created by the remote side and reflects what
                        // fraction of our outgoing packets it has actually received.
                        if (report.type === "remote-inbound-rtp" && report.kind === "video") {
                            totalFractionLost += (_a = report.fractionLost) !== null && _a !== void 0 ? _a : 0;
                            totalRtt += (_b = report.roundTripTime) !== null && _b !== void 0 ? _b : 0;
                            sampleCount++;
                        }
                    });
                }
                catch (_b) {
                    // getStats() can throw when the connection is in a broken state – ignore
                }
            }
            // Fall back to data-channel latency when no video RTP stats are available yet
            if (sampleCount === 0) {
                for (const [, peer] of peers) {
                    const latencyMs = peer.getLatency();
                    if (latencyMs > 0) {
                        totalRtt += latencyMs / 1000;
                        sampleCount++;
                    }
                }
            }
            if (sampleCount === 0)
                return;
            const avgFractionLost = totalFractionLost / sampleCount;
            const avgRtt = totalRtt / sampleCount;
            const isBad = avgFractionLost > PACKET_LOSS_BAD_THRESHOLD || avgRtt > RTT_BAD_S;
            const isGood = avgFractionLost <= PACKET_LOSS_GOOD_THRESHOLD && avgRtt <= RTT_GOOD_S;
            if (isBad) {
                this.badSamples++;
                this.goodSamples = 0;
                if (this.badSamples >= DOWNGRADE_AFTER_N_BAD && this.currentQualityIndex > 0) {
                    this.currentQualityIndex--;
                    this.badSamples = 0;
                    this.applyQualityToAllPeers(this.currentQualityIndex);
                    console.log(`[adaptive-bandwidth] ▼ downgraded to "${QUALITY_LEVELS[this.currentQualityIndex].name}"` +
                        ` (loss=${(avgFractionLost * 100).toFixed(1)}%, rtt=${(avgRtt * 1000).toFixed(0)}ms)`);
                }
            }
            else if (isGood) {
                this.goodSamples++;
                this.badSamples = 0;
                if (this.goodSamples >= UPGRADE_AFTER_N_GOOD && this.currentQualityIndex < QUALITY_LEVELS.length - 1) {
                    this.currentQualityIndex++;
                    this.goodSamples = 0;
                    this.applyQualityToAllPeers(this.currentQualityIndex);
                    console.log(`[adaptive-bandwidth] ▲ upgraded to "${QUALITY_LEVELS[this.currentQualityIndex].name}"`);
                }
            }
            else {
                // Network is neither clearly bad nor clearly good – hold the current level
                this.badSamples = 0;
                this.goodSamples = 0;
            }
        });
    }
    // ── Quality application helpers ───────────────────────────────────────────
    applyQualityToAllPeers(qualityIndex) {
        var _a;
        const peers = (_a = peerService.peerMgr) === null || _a === void 0 ? void 0 : _a.peers;
        if (peers == null)
            return;
        for (const [clientId] of peers) {
            this.applyQualityToPeer(clientId, qualityIndex);
        }
    }
    applyQualityToPeer(clientId, qualityIndex) {
        var _a;
        const peer = (_a = peerService.peerMgr) === null || _a === void 0 ? void 0 : _a.getPeer(clientId);
        if (peer == null)
            return;
        const level = QUALITY_LEVELS[qualityIndex];
        peer.mediaConnections
            .updateBandwidthRestriction(level.maxBitrateKbps, undefined, undefined, "video")
            .catch((e) => { console.error("[adaptive-bandwidth] updateBandwidthRestriction failed", e); });
        peer.mediaConnections
            .setRtpEncodingParametersByKind("video", { scaleResolutionDownBy: level.scaleResolutionDownBy })
            .catch((e) => { console.error("[adaptive-bandwidth] setRtpEncodingParametersByKind failed", e); });
    }
}
export const adaptiveBandwidthService = new AdaptiveBandwidthService();
